import type React from "react"
import { LockOutlined, MailOutlined, PhoneOutlined, UserOutlined } from "@ant-design/icons"
import { Form, Input, Button, Divider, Alert, Typography } from "antd"
import { Link } from "react-router-dom"
import { FcGoogle } from "react-icons/fc"
import { motion, AnimatePresence } from "framer-motion"
import { useState } from "react"
import { useAuth } from "../../context/auth"

const { Title, Paragraph } = Typography;

interface RegisterProps {
  handleToggleForm?: () => void
  closeModal?: () => void
  setForgotPassword?: () => void
}

interface RegisterFormValues {
  name: string
  email: string
  phone: string
  password: string
  confirmPassword: string
  error: string
  loading: boolean
  success: boolean
  successMessage: React.ReactNode
}

const Register: React.FC<RegisterProps> = ({ handleToggleForm }) => {
  const { signUp, signInWithGoogle } = useAuth()
  const [values, setValues] = useState<RegisterFormValues>({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    error: "",
    loading: false,
    success: false,
    successMessage: null,
  })

  const { name, email, phone, password, confirmPassword, error, loading, success, successMessage } = values

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues({ ...values, [e.target.name]: e.target.value })

  const handleSubmit = async () => {
    if (!name || !email || !phone || !password || !confirmPassword) {
      setValues({ ...values, error: "Tous les champs sont obligatoires" })
      return
    }
    if (password !== confirmPassword) {
      setValues({ ...values, error: "Les mots de passe doivent correspondre" })
      return
    }

    setValues({ ...values, error: "", loading: true })

    try {
      const userData = {
        name,
        email,
        phone,
        is_verified: false,
        is_online: true,
        photo_url: "",
      }

      await signUp(email, password, userData)

      setValues({
        name: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        error: "",
        loading: false,
        success: true,
        successMessage: (
          <div className="text-center">
            <motion.div
              className="flex justify-center mb-6"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                <MailOutlined className="text-blue-600 text-3xl" />
              </div>
            </motion.div>
            
            <Title level={3} className="mb-2">Vérifiez votre boîte email!</Title>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Paragraph className="text-gray-600 mb-6">
                Un email de vérification a été envoyé à <span className="font-bold text-blue-600">{email}</span>
              </Paragraph>
              
              <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center mr-3 text-xs">1</div>
                    <span className="text-gray-700">Vérifiez votre boîte de réception et vos spams</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center mr-3 text-xs">2</div>
                    <span className="text-gray-700">Cliquez sur le lien dans l'email</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center mr-3 text-xs">3</div>
                    <span className="text-gray-700">Connectez-vous à votre compte</span>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/auth/login">
                <Button type="primary" size="large" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 font-medium">
                  Aller à la page de connexion
                </Button>
              </Link>
            </motion.div>
          </div>
        ),
      })
    } catch (err) {
      setValues({
        ...values,
        error: err instanceof Error ? err.message : "Erreur lors de l'inscription",
        loading: false,
      })
    }
  }

  const handleGoogleSignUp = async () => {
    try {
      setValues({ ...values, error: "", loading: true });
      await signInWithGoogle();
    } catch (err) {
      setValues({
        ...values,
        error: err instanceof Error ? err.message : "Erreur lors de l'inscription avec Google",
        loading: false,
      });
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="w-full max-w-5xl mx-auto flex flex-col md:flex-row shadow-xl rounded-2xl overflow-hidden"
        style={{ maxHeight: "90vh" }}
      >
        {/* Left side - Gradient background with illustration */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-blue-600 to-purple-600 p-6 justify-center items-center">
          <div className="text-white max-w-md">
            <motion.h1 
              className="text-3xl font-bold mb-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Rejoignez Falashop
            </motion.h1>
            <motion.p 
              className="text-lg opacity-90 mb-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              Créez votre compte en quelques clics.
            </motion.p>
            <motion.div 
              className="bg-white/20 p-4 rounded-xl backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <p className="text-base font-medium mb-2">Avec un compte Falashop:</p>
              <motion.ul className="space-y-1 text-sm">
                {[
                  "Publiez des annonces gratuitement",
                  "Discutez avec les vendeurs",
                  "Sauvegardez vos favoris"
                ].map((item, index) => (
                  <motion.li 
                    key={index}
                    className="flex items-center"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (index * 0.1) }}
                  >
                    <span className="mr-2">✓</span>
                    <span>{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>
          </div>
        </div>

        {/* Right side - Registration form */}
        <div className="w-full md:w-1/2 p-6 bg-white overflow-y-auto" style={{ maxHeight: "90vh" }}>
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white"
            >
              {successMessage}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Title level={2} className="mb-2 primary-gradient-text">Créer un compte</Title>
              <Paragraph className="text-gray-600 mb-4">
                Remplissez le formulaire ci-dessous
              </Paragraph>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4"
                  >
                    <Alert message="Erreur" description={error} type="error" showIcon className="text-left" />
                  </motion.div>
                )}
              </AnimatePresence>

              <Form 
                name="register" 
                onFinish={handleSubmit} 
                layout="vertical" 
                size="middle"
                requiredMark="optional"
                className="space-y-1"
              >
                <Form.Item 
                  name="name" 
                  label={<label className='text-gray-700 font-medium text-sm'>Nom complet</label>}
                  rules={[{ required: true, message: "Veuillez entrer votre nom" }]}
                  className="mb-2"
                >
                  <Input
                    prefix={<UserOutlined className="text-gray-400" />}
                    placeholder="Nom complet"
                    name="name"
                    value={name}
                    onChange={handleChange}
                    className="rounded-lg"
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label={<label className='text-gray-700 font-medium text-sm'>Email</label>}
                  rules={[
                    { required: true, message: "Veuillez entrer votre email" },
                    { type: "email", message: "Email invalide" },
                  ]}
                  className="mb-2"
                >
                  <Input
                    prefix={<MailOutlined className="text-gray-400" />}
                    placeholder="Email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    className="rounded-lg"
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  label={<label className='text-gray-700 font-medium text-sm'>Téléphone</label>}
                  rules={[{ required: true, message: "Veuillez entrer votre numéro de téléphone" }]}
                  className="mb-2"
                >
                  <Input
                    prefix={<PhoneOutlined className="text-gray-400" />}
                    placeholder="Numéro de téléphone"
                    name="phone"
                    value={phone}
                    onChange={handleChange}
                    className="rounded-lg"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={<label className='text-gray-700 font-medium text-sm'>Mot de passe</label>}
                  rules={[
                    { required: true, message: "Veuillez entrer votre mot de passe" },
                    { min: 6, message: "Le mot de passe doit contenir au moins 6 caractères" },
                  ]}
                  className="mb-2"
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder="Mot de passe"
                    name="password"
                    value={password}
                    onChange={handleChange}
                    className="rounded-lg"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label={<label className='text-gray-700 font-medium text-sm'>Confirmer le mot de passe</label>}
                  rules={[
                    { required: true, message: "Veuillez confirmer votre mot de passe" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve()
                        }
                        return Promise.reject(new Error("Les mots de passe ne correspondent pas"))
                      },
                    }),
                  ]}
                  className="mb-3"
                >
                  <Input.Password
                    prefix={<LockOutlined className="text-gray-400" />}
                    placeholder="Confirmer le mot de passe"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={handleChange}
                    className="rounded-lg"
                  />
                </Form.Item>

                <Form.Item className="">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    className="mt-2 w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-10 font-medium"
                  >
                    S'inscrire
                  </Button>
                </Form.Item>
              </Form>

              <Divider plain className="text-gray-500 mb-2">Ou</Divider>

              <motion.div 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mb-3"
              >
                <Button 
                  icon={<FcGoogle className="mr-2" />} 
                  className="w-full border border-gray-300 rounded-lg h-10 font-medium flex items-center justify-center" 
                  onClick={handleGoogleSignUp}
                  loading={loading}
                >
                  Continuer avec Google
                </Button>
              </motion.div>

              <div className="text-center">
                <p className="text-gray-600 text-sm">
                  Avez vous déjà un compte?{" "}
                  {handleToggleForm ? (
                    <Button type="link" onClick={handleToggleForm} className="p-0 primary-gradient-text font-medium">
                      Se connecter
                    </Button>
                  ) : (
                    <Link to="/auth/login" className="primary-gradient-text font-medium hover:text-primary-default">
                      Se connecter
                    </Link>
                  )}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default Register
